import { Code, ConnectError } from "@connectrpc/connect";
import {
  FundSource,
  HokkaidoR8HouseholderCodeStatus,
  HokkaidoR8HouseholderMynaApplicationResult,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import {
  type CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceRequest,
  CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceResponse,
  type GetHokkaidoR8HouseholderCodeStatusRequest,
  GetHokkaidoR8HouseholderCodeStatusResponse,
  type GetHokkaidoR8HouseholderMynaApplicationRequest,
  GetHokkaidoR8HouseholderMynaApplicationResponse,
  type HandleHokkaidoR8HouseholderMynaApplicationCallbackRequest,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Completed,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason,
  HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Pending,
  type StartHokkaidoR8HouseholderMynaApplicationRequest,
  StartHokkaidoR8HouseholderMynaApplicationResponse,
} from "schema/gen/es/chiikipoint/wallet/v2/service_pb";

type MockCode = {
  status: HokkaidoR8HouseholderCodeStatus;
};

// Mock keys mirror the production canonical form: compact 12-char with no
// hyphens. Routes that hit this mock already pass codes in canonical form.
const mockCodes = new Map<string, MockCode>([
  ["222ABCDEFGHJ", { status: HokkaidoR8HouseholderCodeStatus.UNUSED }],
  [
    "222KKKKKKKKK",
    { status: HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE },
  ],
  [
    "222VVVVVVVVV",
    { status: HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER },
  ],
]);

// Reserved code that always throws Code.Internal, used to verify the
// ErrorBoundary path (ErrorPage + Sentry).
const INTERNAL_ERROR_CODE = "222INTERNAL0";

const hokkaidoFundSource = new FundSource({
  id: "hokkaido-householder",
  name: "道民生活応援ポイント",
  priorityWeight: BigInt(1),
  forbidTransfer: true,
});

const hokkaidoAmount = BigInt(5_500);

export async function handleGetHokkaidoR8HouseholderCodeStatus(
  req: GetHokkaidoR8HouseholderCodeStatusRequest,
) {
  await new Promise((r) => setTimeout(r, 200));
  if (req.code === INTERNAL_ERROR_CODE) {
    throw new ConnectError("simulated internal error", Code.Internal);
  }
  const found = mockCodes.get(req.code);
  if (!found) {
    // Spec: PermissionDenied to avoid code enumeration.
    throw new ConnectError("not usable", Code.PermissionDenied);
  }
  return new GetHokkaidoR8HouseholderCodeStatusResponse({
    status: found.status,
    amount: hokkaidoAmount,
    fundSource: hokkaidoFundSource,
  });
}

export async function handleCreateHokkaidoR8HouseholderCodeUseAsWalletBalance(
  req: CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceRequest,
) {
  await new Promise((r) => setTimeout(r, 400));
  if (req.code === INTERNAL_ERROR_CODE) {
    throw new ConnectError("simulated internal error", Code.Internal);
  }
  const found = mockCodes.get(req.code);
  if (!found) {
    throw new ConnectError("not usable", Code.PermissionDenied);
  }
  if (found.status === HokkaidoR8HouseholderCodeStatus.USED_AS_PAPER_VOUCHER) {
    throw new ConnectError(
      "code already used as paper voucher",
      Code.FailedPrecondition,
    );
  }
  if (found.status === HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE) {
    throw new ConnectError(
      "code already used as wallet balance",
      Code.FailedPrecondition,
    );
  }
  found.status = HokkaidoR8HouseholderCodeStatus.USED_AS_WALLET_BALANCE;
  return new CreateHokkaidoR8HouseholderCodeUseAsWalletBalanceResponse({
    amount: hokkaidoAmount,
    fundSource: hokkaidoFundSource,
  });
}

// MynaConnect への遷移で画面が再読み込みされるため、モックの状態は sessionStorage に持つ。
// 認証結果は sessionStorage の MOCK_MYNA_RESULT_KEY で切り替える
// （Result の enum 名 例: "NOT_HOUSEHOLDER"、または "failed" / "error"。未設定なら GRANTED）
const MOCK_MYNA_STATE_KEY = "mock-hokkaido-myna-application";
const MOCK_MYNA_RESULT_KEY = "mock-hokkaido-myna-result";

type MockMynaState = {
  result?: HokkaidoR8HouseholderMynaApplicationResult;
  received: boolean;
};

function readMockMynaState(): MockMynaState {
  try {
    const raw = sessionStorage.getItem(MOCK_MYNA_STATE_KEY);
    if (raw) return JSON.parse(raw) as MockMynaState;
  } catch {
    // 読めなければ未申請として扱う
  }
  return { received: false };
}

function writeMockMynaState(state: MockMynaState) {
  sessionStorage.setItem(MOCK_MYNA_STATE_KEY, JSON.stringify(state));
}

let mynaCallbackCalls = 0;

export async function handleGetHokkaidoR8HouseholderMynaApplication(
  _req: GetHokkaidoR8HouseholderMynaApplicationRequest,
) {
  const state = readMockMynaState();
  return new GetHokkaidoR8HouseholderMynaApplicationResponse({
    received: state.received,
    result: state.result,
  });
}

export async function handleStartHokkaidoR8HouseholderMynaApplication(
  _req: StartHokkaidoR8HouseholderMynaApplicationRequest,
) {
  await new Promise((r) => setTimeout(r, 400));
  if (readMockMynaState().received) {
    return new StartHokkaidoR8HouseholderMynaApplicationResponse({
      outcome: { case: "alreadyReceived", value: true },
    });
  }
  return new StartHokkaidoR8HouseholderMynaApplicationResponse({
    outcome: { case: "redirectUri", value: "/hkp/apply/callback" },
  });
}

export async function handleHandleHokkaidoR8HouseholderMynaApplicationCallback(
  _req: HandleHokkaidoR8HouseholderMynaApplicationCallbackRequest,
) {
  // 実機では MynaConnect の処理待ちが挟まるので、数回 pending を返してポーリングを再現する
  mynaCallbackCalls += 1;
  if (mynaCallbackCalls % 3 !== 0) {
    return new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse({
      outcome: {
        case: "pending",
        value:
          new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Pending(),
      },
    });
  }

  const resultKey = sessionStorage.getItem(MOCK_MYNA_RESULT_KEY) ?? "GRANTED";
  if (resultKey === "error") {
    throw new ConnectError("simulated internal error", Code.Internal);
  }
  if (resultKey === "failed") {
    return new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse({
      outcome: {
        case: "failed",
        value:
          new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed(
            {
              reason:
                HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Failed_Reason.EXPIRED,
            },
          ),
      },
    });
  }

  const result =
    HokkaidoR8HouseholderMynaApplicationResult[
      resultKey as keyof typeof HokkaidoR8HouseholderMynaApplicationResult
    ] ?? HokkaidoR8HouseholderMynaApplicationResult.GRANTED;
  const granted =
    result === HokkaidoR8HouseholderMynaApplicationResult.GRANTED ||
    result === HokkaidoR8HouseholderMynaApplicationResult.STAFF_GRANTED;
  const alreadyReceived =
    result === HokkaidoR8HouseholderMynaApplicationResult.ALREADY_RECEIVED;
  writeMockMynaState({
    // ALREADY_RECEIVED は申請行に保存されない
    result: alreadyReceived ? undefined : result,
    received: granted || alreadyReceived,
  });
  return new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse({
    outcome: {
      case: "completed",
      value:
        new HandleHokkaidoR8HouseholderMynaApplicationCallbackResponse_Completed(
          {
            result,
            amount: granted ? hokkaidoAmount : BigInt(0),
            fundSource: granted ? hokkaidoFundSource : undefined,
          },
        ),
    },
  });
}
